import { Event } from 'react-big-calendar';
import { AppointmentStatusKey } from '../../helpers/appointments';

export interface Resource {
  resourceId: string;
  resourceTitle: string;
  color?: string;
  // True for offboarded employees whose lane only shows because they still
  // have appointments in the visible range. These lanes are not subject to
  // the `selectedArtists` filter.
  isInactive?: boolean;
  // Column-header avatar (redesign only). Image wins; initials fall back.
  profileImage?: string;
  initials?: string;
}

export type CalendarEvent = Omit<Event, 'resourceId'> & {
  resourceId: string;
  detailsComponent?: JSX.Element;
  color?: string;
  textColor?: string;
} & (
    | {
        type: 'appointment';
        appointmentId: string;
        canceled?: boolean;
        checkedOut?: boolean;
        serviceCategory?: string;
        appointmentType?: string;
        isVirtual?: boolean;
        statusKey?: AppointmentStatusKey;
        // null: waiverRequest exists but resolves to no color (shouldn't
        // happen). undefined: no waiverRequest, so no icon renders.
        waiverIconColor?: string | null;
        // Redesign only: the left rail takes the artist's color.
        railColor?: string;
        // Rendered in whichever of the two price positions (summary row /
        // time row) fits the block's current height.
        priceText?: string;
        // Follows price's position rather than having its own placement,
        // per README: "Minor... merges into the price line first".
        isMinor?: boolean;
      }
    | {
        type: 'time-block';
        timeBlockId: number;
      }
    | {
        type: 'trip';
        tripId: string;
      }
    | {
        type: 'flash-day';
        flashDayId: number;
      }
    | {
        type: 'unavailable-time';
        unavailableTimeId: string;
        // Whether to render the "Google" source-label pill.
        showSourceLabel?: boolean;
      }
  );

export interface CalendarSlotSelection {
  start: Date;
  // Set when the user drag-selected a range rather than clicking a single slot.
  end?: Date;
  employeeId?: string;
}

export const CALENDAR_EVENT_TYPES = {
  SCHEDULED_APPOINTMENTS: 'scheduled-appointments',
  CANCELLED_APPOINTMENTS: 'cancelled-appointments',
  TIME_BLOCKS: 'time-blocks',
  TRIPS: 'trips',
  FLASH_DAYS: 'flash-days',
  UNAVAILABLE_TIMES: 'unavailable-times',
} as const;

export type CalendarEventType =
  (typeof CALENDAR_EVENT_TYPES)[keyof typeof CALENDAR_EVENT_TYPES];

export const DEPOSIT_STATUS_VALUES = {
  ALL: 'ALL',
  PAID: 'PAID',
  UNPAID: 'UNPAID',
  NO_DEPOSIT: 'NO_DEPOSIT',
} as const;

export type DepositFilterStatus =
  (typeof DEPOSIT_STATUS_VALUES)[keyof typeof DEPOSIT_STATUS_VALUES];
